import type { Disposable } from "@/shared/contracts/lifecycle";
import { getMessages } from "@/i18n/runtime";
import {
  HOV_TIM_GAIN_DISPLAY_VALUE,
  type HovTimBalancePair,
  type HovTimBalanceTarget,
} from "@/features/hoverIdle/core/hoverIdleModel";
import type { HoverIdleSnapshot } from "../types";
import {
  createUpgradeHoverRepeater,
  type UpgradeHoverRepeater,
} from "@/features/hoverIdle/hover/upgradeHoverRepeater";
import type { UpgradeRepeatSpeedMultiplierSource } from "@/features/hoverIdle/upgrades/upgradeRepeatTiming";
import {
  formatForContext,
  formatTwoDecimals,
} from "@/features/hoverIdle/resources/numberFormat";

export type HovTimBalanceControlId = "resource" | "gain";

interface HovTimBalanceViewOptions {
  readonly container: HTMLElement;
  readonly onAssign: (target: HovTimBalanceTarget) => void;
  readonly onUnassign: (target: HovTimBalanceTarget) => void;
  readonly onRatioChange: (pair: HovTimBalancePair, ratio: number) => void;
  readonly onNotificationChange?: (hasUnassigned: boolean) => void;
  readonly speedMultiplier?: UpgradeRepeatSpeedMultiplierSource;
}

const ASSIGN_INITIAL_INTERVAL_MS = 600;
const ASSIGN_MIN_INTERVAL_MS = 120;
const ASSIGN_DECAY = 0.82;

export class HovTimBalanceView implements Disposable {
  private readonly root: HTMLDivElement;
  private readonly totalLine: HTMLDivElement;
  private readonly totalGainLine: HTMLDivElement;
  private readonly unassignedLine: HTMLDivElement;

  private readonly resourceControl: AllocationControl;
  private readonly gainControl: AllocationControl;

  private readonly onNotificationChange?: (hasUnassigned: boolean) => void;
  private total = 0;
  private unassigned = 0;
  private lastNotifiedHasUnassigned = false;

  constructor(options: HovTimBalanceViewOptions) {
    const messages = getMessages();
    this.onNotificationChange = options.onNotificationChange;
    this.root = document.createElement("div");
    this.root.className = "hover-idle-balance-panel";
    this.root.setAttribute("role", "region");
    this.root.setAttribute("aria-label", messages.balance.regionAria);
    this.root.hidden = true;

    this.totalLine = document.createElement("div");
    this.totalLine.className = "hover-idle-balance-line";
    this.totalGainLine = document.createElement("div");
    this.totalGainLine.className = "hover-idle-balance-line";
    this.unassignedLine = document.createElement("div");
    this.unassignedLine.className = "hover-idle-balance-line";

    this.resourceControl = new AllocationControl({
      leftLabel: "Hov",
      rightLabel: "Tim",
      ariaLabel: messages.balance.resourceSliderAria,
      onAssignFire: () =>
        options.onAssign(this.resourceControl.ratio < 0.5 ? "hov" : "tim"),
      onUnassignFire: () =>
        options.onUnassign(
          this.resourceControl.preferredUnassignTarget("hov", "tim"),
        ),
      onRatioChange: (ratio) => options.onRatioChange("resource", ratio),
      speedMultiplier: options.speedMultiplier,
    });

    this.gainControl = new AllocationControl({
      leftLabel: "HG",
      rightLabel: "TG",
      ariaLabel: messages.balance.gainSliderAria,
      onAssignFire: () =>
        options.onAssign(this.gainControl.ratio < 0.5 ? "hovGain" : "timGain"),
      onUnassignFire: () =>
        options.onUnassign(
          this.gainControl.preferredUnassignTarget("hovGain", "timGain"),
        ),
      onRatioChange: (ratio) => options.onRatioChange("gain", ratio),
      speedMultiplier: options.speedMultiplier,
    });

    this.root.append(
      this.totalLine,
      this.totalGainLine,
      this.unassignedLine,
      this.resourceControl.section,
      this.gainControl.section,
    );

    options.container.append(this.root);
    this.render();
  }

  setVisible(visible: boolean): void {
    this.root.hidden = !visible;
    if (!visible) {
      this.resourceControl.disengage();
      this.gainControl.disengage();
    }
  }

  update(snapshot: HoverIdleSnapshot): void {
    const balance = snapshot.hovTimBalance;
    this.total = balance.total;
    this.unassigned = balance.unassigned;
    this.resourceControl.setAllocations(
      balance.hovAllocation,
      balance.timAllocation,
    );
    this.gainControl.setAllocations(
      balance.hovGainAllocation,
      balance.timGainAllocation,
    );
    this.gainControl.setVisible(snapshot.unlocks.tabs.hovTimGainBalance);
    this.render();
  }

  dispose(): void {
    this.resourceControl.dispose();
    this.gainControl.dispose();
    this.root.remove();
  }

  private render(): void {
    const messages = getMessages();
    this.totalLine.textContent = messages.balance.total(
      formatForContext(this.total, "tooltip"),
    );
    this.totalGainLine.textContent = messages.balance.gain(
      formatTwoDecimals(HOV_TIM_GAIN_DISPLAY_VALUE),
    );
    this.unassignedLine.textContent = messages.balance.unassigned(
      formatForContext(this.unassigned, "tooltip"),
    );
    const canAssignFromPool = this.unassigned > 0;
    this.resourceControl.renderSelf(canAssignFromPool);
    this.gainControl.renderSelf(canAssignFromPool);

    const hasUnassigned = this.unassigned > 0;
    if (hasUnassigned !== this.lastNotifiedHasUnassigned) {
      this.lastNotifiedHasUnassigned = hasUnassigned;
      this.onNotificationChange?.(hasUnassigned);
    }
  }
}

interface AllocationControlOptions {
  readonly leftLabel: string;
  readonly rightLabel: string;
  readonly ariaLabel: string;
  readonly onAssignFire: () => void;
  readonly onUnassignFire: () => void;
  readonly onRatioChange: (ratio: number) => void;
  readonly speedMultiplier?: UpgradeRepeatSpeedMultiplierSource;
}

class AllocationControl implements Disposable {
  readonly section: HTMLDivElement;
  private readonly track: HTMLDivElement;
  private readonly fillLeft: HTMLDivElement;
  private readonly fillRight: HTMLDivElement;
  private readonly handle: HTMLDivElement;
  private readonly readout: HTMLDivElement;
  private readonly readoutLeft: HTMLSpanElement;
  private readonly readoutRight: HTMLSpanElement;
  private readonly assignButton: HTMLDivElement;
  private readonly unassignButton: HTMLDivElement;
  private readonly assignRepeater: UpgradeHoverRepeater;
  private readonly unassignRepeater: UpgradeHoverRepeater;

  private leftAllocation = 0;
  private rightAllocation = 0;
  private _ratio = 0.5;
  private readonly onRatioChange: (ratio: number) => void;

  private readonly handleTrackPointerMove = (event: PointerEvent): void => {
    this.setRatioFromPointer(event.clientX);
  };

  private readonly handleTrackPointerEnter = (event: PointerEvent): void => {
    this.track.dataset.hovering = "true";
    this.setRatioFromPointer(event.clientX);
  };

  private readonly handleTrackPointerLeave = (): void => {
    this.track.dataset.hovering = "false";
  };

  private readonly handleAssignPointerEnter = (): void => {
    this.assignButton.dataset.engaged = "true";
    this.assignRepeater.setEngaged(true);
  };

  private readonly handleAssignPointerLeave = (): void => {
    this.assignButton.dataset.engaged = "false";
    this.assignRepeater.setEngaged(false);
  };

  private readonly handleUnassignPointerEnter = (): void => {
    this.unassignButton.dataset.engaged = "true";
    this.unassignRepeater.setEngaged(true);
  };

  private readonly handleUnassignPointerLeave = (): void => {
    this.unassignButton.dataset.engaged = "false";
    this.unassignRepeater.setEngaged(false);
  };

  constructor(options: AllocationControlOptions) {
    this.onRatioChange = options.onRatioChange;
    this.section = document.createElement("div");
    this.section.className = "hover-idle-balance-section";

    const heading = document.createElement("div");
    heading.className = "hover-idle-balance-section-heading";

    const slider = createAllocationSlider(
      options.leftLabel,
      options.rightLabel,
      options.ariaLabel,
    );
    this.track = slider.track;
    this.fillLeft = slider.fillLeft;
    this.fillRight = slider.fillRight;
    this.handle = slider.handle;
    this.readout = slider.readout;
    this.readoutLeft = slider.readoutLeft;
    this.readoutRight = slider.readoutRight;

    const controls = createAssignButtonRow();
    this.assignButton = controls.assignButton;
    this.unassignButton = controls.unassignButton;

    this.section.append(heading, this.readout, this.track, controls.buttonsRow);

    this.assignRepeater = createUpgradeHoverRepeater({
      onFire: options.onAssignFire,
      initialIntervalMs: ASSIGN_INITIAL_INTERVAL_MS,
      minIntervalMs: ASSIGN_MIN_INTERVAL_MS,
      decay: ASSIGN_DECAY,
      speedMultiplier: options.speedMultiplier,
    });
    this.unassignRepeater = createUpgradeHoverRepeater({
      onFire: options.onUnassignFire,
      initialIntervalMs: ASSIGN_INITIAL_INTERVAL_MS,
      minIntervalMs: ASSIGN_MIN_INTERVAL_MS,
      decay: ASSIGN_DECAY,
      speedMultiplier: options.speedMultiplier,
    });

    this.track.addEventListener("pointerenter", this.handleTrackPointerEnter);
    this.track.addEventListener("pointermove", this.handleTrackPointerMove);
    this.track.addEventListener("pointerleave", this.handleTrackPointerLeave);
    this.assignButton.addEventListener(
      "pointerenter",
      this.handleAssignPointerEnter,
    );
    this.assignButton.addEventListener(
      "pointerleave",
      this.handleAssignPointerLeave,
    );
    this.unassignButton.addEventListener(
      "pointerenter",
      this.handleUnassignPointerEnter,
    );
    this.unassignButton.addEventListener(
      "pointerleave",
      this.handleUnassignPointerLeave,
    );
  }

  get ratio(): number {
    return this._ratio;
  }

  get assigned(): number {
    return this.leftAllocation + this.rightAllocation;
  }

  setAllocations(leftAllocation: number, rightAllocation: number): void {
    this.leftAllocation = Math.max(0, Math.floor(leftAllocation));
    this.rightAllocation = Math.max(0, Math.floor(rightAllocation));
    const total = this.leftAllocation + this.rightAllocation;
    if (total > 0) {
      this._ratio = clamp01(this.rightAllocation / total);
    }
  }

  preferredUnassignTarget<A, B>(left: A, right: B): A | B {
    if (this.rightAllocation <= 0) return left;
    if (this.leftAllocation <= 0) return right;
    return this._ratio < 0.5 ? left : right;
  }

  renderSelf(canAssignFromPool: boolean): void {
    const leftPercent = Math.round((1 - this._ratio) * 100);
    const rightPercent = 100 - leftPercent;
    this.fillLeft.style.width = `${leftPercent}%`;
    this.fillRight.style.width = `${rightPercent}%`;
    this.handle.style.left = `${this._ratio * 100}%`;
    this.track.setAttribute("aria-valuenow", String(rightPercent));
    this.track.setAttribute(
      "aria-valuetext",
      `${this.leftAllocation} - ${this.rightAllocation}`,
    );

    const canUnassign = this.assigned > 0;
    this.assignButton.dataset.disabled = canAssignFromPool ? "false" : "true";
    this.unassignButton.dataset.disabled = canUnassign ? "false" : "true";

    this.readoutLeft.textContent = String(this.leftAllocation);
    this.readoutRight.textContent = String(this.rightAllocation);
  }

  disengage(): void {
    this.assignRepeater.setEngaged(false);
    this.unassignRepeater.setEngaged(false);
    this.assignButton.dataset.engaged = "false";
    this.unassignButton.dataset.engaged = "false";
  }

  setVisible(visible: boolean): void {
    this.section.hidden = !visible;
    if (!visible) {
      this.disengage();
    }
  }

  dispose(): void {
    this.track.removeEventListener(
      "pointerenter",
      this.handleTrackPointerEnter,
    );
    this.track.removeEventListener("pointermove", this.handleTrackPointerMove);
    this.track.removeEventListener(
      "pointerleave",
      this.handleTrackPointerLeave,
    );
    this.assignButton.removeEventListener(
      "pointerenter",
      this.handleAssignPointerEnter,
    );
    this.assignButton.removeEventListener(
      "pointerleave",
      this.handleAssignPointerLeave,
    );
    this.unassignButton.removeEventListener(
      "pointerenter",
      this.handleUnassignPointerEnter,
    );
    this.unassignButton.removeEventListener(
      "pointerleave",
      this.handleUnassignPointerLeave,
    );
    this.assignRepeater.dispose();
    this.unassignRepeater.dispose();
  }

  private setRatioFromPointer(clientX: number): void {
    const rect = this.track.getBoundingClientRect();
    if (rect.width <= 0) {
      return;
    }
    const relative = (clientX - rect.left) / rect.width;
    const clamped = clamp01(relative);
    if (clamped === this._ratio) {
      return;
    }
    this._ratio = clamped;
    this.onRatioChange(clamped);
    this.renderSelf(this.assignButton.dataset.disabled !== "true");
  }
}

function createAllocationSlider(
  leftLabel: string,
  rightLabel: string,
  ariaLabel: string,
): {
  readonly track: HTMLDivElement;
  readonly fillLeft: HTMLDivElement;
  readonly fillRight: HTMLDivElement;
  readonly handle: HTMLDivElement;
  readonly readout: HTMLDivElement;
  readonly readoutLeft: HTMLSpanElement;
  readonly readoutRight: HTMLSpanElement;
} {
  const track = document.createElement("div");
  track.className = "hover-idle-balance-slider";
  track.setAttribute("role", "slider");
  track.setAttribute("aria-label", ariaLabel);
  track.setAttribute("aria-valuemin", "0");
  track.setAttribute("aria-valuemax", "100");
  track.dataset.hovering = "false";

  const labelLeft = document.createElement("span");
  labelLeft.className = "hover-idle-balance-slider-label";
  labelLeft.dataset.side = "left";
  labelLeft.textContent = leftLabel;

  const labelRight = document.createElement("span");
  labelRight.className = "hover-idle-balance-slider-label";
  labelRight.dataset.side = "right";
  labelRight.textContent = rightLabel;

  const fillLeft = document.createElement("div");
  fillLeft.className = "hover-idle-balance-slider-fill";
  fillLeft.dataset.side = "left";

  const fillRight = document.createElement("div");
  fillRight.className = "hover-idle-balance-slider-fill";
  fillRight.dataset.side = "right";

  const handle = document.createElement("div");
  handle.className = "hover-idle-balance-slider-handle";

  track.append(labelLeft, fillLeft, fillRight, handle, labelRight);

  const readout = document.createElement("div");
  readout.className = "hover-idle-balance-slider-readout";
  const readoutLeft = document.createElement("span");
  readoutLeft.className = "hover-idle-balance-slider-readout-value";
  readoutLeft.dataset.side = "left";
  const readoutRight = document.createElement("span");
  readoutRight.className = "hover-idle-balance-slider-readout-value";
  readoutRight.dataset.side = "right";
  readout.append(readoutLeft, readoutRight);

  return {
    track,
    fillLeft,
    fillRight,
    handle,
    readout,
    readoutLeft,
    readoutRight,
  };
}

function createAssignButtonRow(): {
  readonly buttonsRow: HTMLDivElement;
  readonly assignButton: HTMLDivElement;
  readonly unassignButton: HTMLDivElement;
} {
  const messages = getMessages();
  const buttonsRow = document.createElement("div");
  buttonsRow.className = "hover-idle-balance-assign-row";

  const unassignButton = document.createElement("div");
  unassignButton.className = "hover-idle-balance-assign-button";
  unassignButton.dataset.action = "unassign";
  unassignButton.dataset.engaged = "false";
  unassignButton.setAttribute("role", "button");
  unassignButton.setAttribute("aria-label", messages.balance.unassignAria);
  unassignButton.tabIndex = 0;
  const unassignLabel = document.createElement("span");
  unassignLabel.className = "hover-idle-balance-assign-button-label";
  unassignLabel.textContent = messages.balance.unassign;
  const unassignHint = document.createElement("span");
  unassignHint.className = "hover-idle-balance-assign-button-hint";
  unassignHint.textContent = messages.balance.unassignHint;
  unassignButton.append(unassignLabel, unassignHint);

  const assignButton = document.createElement("div");
  assignButton.className = "hover-idle-balance-assign-button";
  assignButton.dataset.action = "assign";
  assignButton.dataset.engaged = "false";
  assignButton.setAttribute("role", "button");
  assignButton.setAttribute("aria-label", messages.balance.assignAria);
  assignButton.tabIndex = 0;
  const assignLabel = document.createElement("span");
  assignLabel.className = "hover-idle-balance-assign-button-label";
  assignLabel.textContent = messages.balance.assign;
  const assignHint = document.createElement("span");
  assignHint.className = "hover-idle-balance-assign-button-hint";
  assignHint.textContent = messages.balance.assignHint;
  assignButton.append(assignLabel, assignHint);

  buttonsRow.append(unassignButton, assignButton);
  return { buttonsRow, assignButton, unassignButton };
}

function clamp01(value: number): number {
  if (Number.isNaN(value)) {
    return 0.5;
  }
  return Math.max(0, Math.min(1, value));
}
